import type { TaskBlock, TaskTemplate, TaskTemplateInput } from '@pm/schemas';
import { Badge, Button } from '@pm/ui';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { api } from '@/lib/api';
import { useInvalidating, wfKeys } from '@/lib/queries';
import { useToast } from '@/lib/toast';
import { TaskTemplateDialog } from './TaskTemplateDialog';

/** Edição dos templates de um bloco reutilizável. */
export function BlockEditor({ block }: { block: TaskBlock }) {
  const toast = useToast();
  const [editing, setEditing] = useState<TaskTemplate | 'new' | null>(null);
  const inval = [wfKeys.blocks];
  const save = useInvalidating(
    async ({ id, input, deps }: { id?: string; input: TaskTemplateInput; deps: string[] }) => {
      if (id) {
        await api.patch(`/task-templates/${id}`, input);
        return api.put(`/task-templates/${id}/dependencies`, {
          items: deps.map((d) => ({ depends_on_task_template_id: d, is_required: true })),
        });
      }
      return api.post(`/task-blocks/${block.id}/templates`, input);
    },
    inval,
  );
  const remove = useInvalidating((id: string) => api.delete(`/task-templates/${id}`), inval);

  return (
    <div className="flex flex-col gap-2">
      <TemplateList
        templates={block.templates}
        onEdit={setEditing}
        onRemove={(id) => remove.mutate(id, { onError: (e) => toast.error(e) })}
      />
      <Button
        variant="secondary"
        size="sm"
        className="self-start"
        onClick={() => setEditing('new')}
      >
        <Plus /> Tarefa no bloco
      </Button>
      <TaskTemplateDialog
        open={editing !== null}
        onOpenChange={(o) => !o && setEditing(null)}
        template={editing === 'new' ? undefined : (editing ?? undefined)}
        siblings={block.templates}
        loading={save.isPending}
        onSubmit={(input, deps) =>
          save.mutate(
            { id: editing && editing !== 'new' ? editing.id : undefined, input, deps },
            {
              onSuccess: () => (toast.success('Template salvo'), setEditing(null)),
              onError: (e) => toast.error(e),
            },
          )
        }
      />
    </div>
  );
}

export function TemplateList({
  templates,
  onEdit,
  onRemove,
  readOnly,
}: {
  templates: TaskTemplate[];
  onEdit?: (t: TaskTemplate) => void;
  onRemove?: (id: string) => void;
  readOnly?: boolean;
}) {
  if (templates.length === 0) return <p className="text-sm text-text-3">Sem tarefas.</p>;
  return (
    <ul className="flex flex-col divide-y divide-border rounded border border-border">
      {templates.map((t) => (
        <li key={t.id} className="flex items-center gap-2 px-3 py-1.5 text-sm">
          <span className="font-mono text-xs text-text-3">{t.sort_order + 1}</span>
          <span className="font-medium">{t.title}</span>
          {t.function ? (
            <Badge mono={false} tone="info">
              {t.function.name}
            </Badge>
          ) : (
            <Badge tone="neutral">sem função</Badge>
          )}
          {t.sla_value !== null ? (
            <span className="font-mono text-xs text-text-3">
              SLA {t.sla_value}{' '}
              {t.sla_unit === 'BUSINESS_DAYS' ? 'd.u.' : t.sla_unit === 'DAYS' ? 'd' : 'h'}
            </span>
          ) : null}
          {t.requires_approval ? <Badge tone="violet">aprovação</Badge> : null}
          {!t.is_required ? <Badge tone="neutral">opcional</Badge> : null}
          {t.depends_on.length ? (
            <span className="font-mono text-xs text-text-3">depende de {t.depends_on.length}</span>
          ) : null}
          {t.source_block_id ? <Badge tone="accent">de bloco</Badge> : null}
          {!readOnly ? (
            <span className="ml-auto flex gap-1">
              {onEdit ? (
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Editar"
                  onClick={() => onEdit(t)}
                >
                  <Pencil />
                </Button>
              ) : null}
              {onRemove ? (
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Remover"
                  onClick={() => onRemove(t.id)}
                >
                  <Trash2 />
                </Button>
              ) : null}
            </span>
          ) : null}
        </li>
      ))}
    </ul>
  );
}
